/**
 * ImpactClassificationPicker — J2-C
 *
 * Reusable radio-group component for the 4 ROROS impact classifications.
 * Each option shows: label + 1-line definition + 1-line example.
 *
 * Required field — the parent form must not submit when no value is selected.
 *
 * Built to be reused in J5's full Decision Note form unchanged.
 *
 * PRD §9.12.2 + §12.3.5 task 4.
 */

import React from 'react';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

// ─── Impact level definitions ─────────────────────────────────────────────────

export type ImpactClassification = 'root' | 'trunk' | 'branch' | 'leaf';

interface ImpactOption {
  value: ImpactClassification;
  label: string;
  definition: string;
  example: string;
  colorClass: string;
  activeClass: string;
}

const IMPACT_OPTIONS: ImpactOption[] = [
  {
    value: 'root',
    label: 'Root',
    definition: t('Existential — alters the company\'s fundamental direction'),
    example: t('"Pivot the company"'),
    colorClass: 'text-error-700 bg-error-50 border-error-200',
    activeClass: 'border-error-500 ring-2 ring-error-300 bg-error-50',
  },
  {
    value: 'trunk',
    label: 'Trunk',
    definition: t('Strategic — shapes the next 6–12 months'),
    example: t('"Choose the next product to build"'),
    colorClass: 'text-warning-700 bg-warning-50 border-warning-200',
    activeClass: 'border-warning-500 ring-2 ring-warning-300 bg-warning-50',
  },
  {
    value: 'branch',
    label: 'Branch',
    definition: t('Tactical — affects a domain or team'),
    example: t('"Switch CRM vendor"'),
    colorClass: 'text-info-700 bg-info-50 border-info-200',
    activeClass: 'border-info-500 ring-2 ring-info-300 bg-info-50',
  },
  {
    value: 'leaf',
    label: 'Leaf',
    definition: t('Operational — day-to-day'),
    example: t('"Buy software for $200"'),
    colorClass: 'text-foreground bg-muted border-border',
    activeClass: 'border-primary-500 ring-2 ring-primary-300 bg-primary-50',
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

export interface ImpactClassificationPickerProps {
  /** Currently selected classification or undefined when unset */
  value?: ImpactClassification | string;
  /** Called when user selects an option */
  onChange: (value: ImpactClassification) => void;
  /** Error message shown below the picker (e.g. when field is required) */
  error?: string | null;
  /** When true, renders a compact horizontal row instead of a 2×2 grid */
  compact?: boolean;
  /** Disables all options */
  disabled?: boolean;
}

export function ImpactClassificationPicker({
  value,
  onChange,
  error,
  compact = false,
  disabled = false,
}: ImpactClassificationPickerProps) {
  const handleKey = (e: React.KeyboardEvent, opt: ImpactOption) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault();
      onChange(opt.value);
    }
    // Arrow key navigation
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const idx = IMPACT_OPTIONS.findIndex((o) => o.value === opt.value);
      const next = IMPACT_OPTIONS[(idx + 1) % IMPACT_OPTIONS.length];
      onChange(next.value);
      (e.currentTarget.parentElement?.children[
        (idx + 1) % IMPACT_OPTIONS.length
      ] as HTMLElement)?.focus();
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const idx = IMPACT_OPTIONS.findIndex((o) => o.value === opt.value);
      const prev = IMPACT_OPTIONS[(idx - 1 + IMPACT_OPTIONS.length) % IMPACT_OPTIONS.length];
      onChange(prev.value);
      (e.currentTarget.parentElement?.children[
        (idx - 1 + IMPACT_OPTIONS.length) % IMPACT_OPTIONS.length
      ] as HTMLElement)?.focus();
    }
  };

  return (
    <div>
      <div
        role="radiogroup"
        aria-label={t('Impact Classification')}
        aria-required="true"
        className={cn(
          compact
            ? 'flex flex-row gap-2'
            : 'grid grid-cols-2 gap-2',
        )}
      >
        {IMPACT_OPTIONS.map((opt) => {
          const isSelected = value === opt.value;
          return (
            <div
              key={opt.value}
              role="radio"
              aria-checked={isSelected}
              aria-label={`${opt.label} — ${opt.definition}`}
              tabIndex={disabled ? -1 : 0}
              onClick={() => !disabled && onChange(opt.value)}
              onKeyDown={(e) => !disabled && handleKey(e, opt)}
              className={cn(
                'rounded-lg border p-3 cursor-pointer select-none transition-all',
                'focus:outline-none',
                disabled && 'opacity-50 cursor-not-allowed',
                isSelected ? opt.activeClass : `border-border bg-card hover:border-primary-300`,
                compact && 'flex-1',
              )}
            >
              <div className="flex items-start gap-2">
                {/* Radio indicator */}
                <span
                  aria-hidden
                  className={cn(
                    'flex-shrink-0 mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center',
                    isSelected ? 'border-primary-600' : 'border-border',
                  )}
                >
                  {isSelected && (
                    <span className="h-2 w-2 rounded-full bg-primary-600 block" />
                  )}
                </span>

                <div className="min-w-0">
                  <span
                    className={cn(
                      'block text-sm font-semibold',
                      isSelected ? 'text-foreground' : 'text-foreground',
                    )}
                  >
                    {opt.label}
                  </span>
                  {!compact && (
                    <>
                      <span className="block text-xs text-muted-foreground leading-snug mt-0.5">
                        {opt.definition}
                      </span>
                      <span className="block text-xs text-muted-foreground/70 italic mt-0.5">
                        {opt.example}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Error message */}
      {error && (
        <p role="alert" className="mt-1.5 text-xs text-error-600 font-medium">
          {error}
        </p>
      )}
    </div>
  );
}
